/**
 * Batch lifecycle controls (Layer 3).
 *
 * Every state-machine action in the batch-detail header. Mirrors the BE state
 * machine (`compliance/batches/state_machine.py`):
 *
 *   Open ──▶ PendingApproval ──▶ Approved
 *    ▲____________|____________|   (send-backs, both land on Open)
 *
 * All three edges are `POST /transition`. Approving used to have a dedicated
 * sign-off button that captured a signature and froze the figures; both are
 * gone, so it is an ordinary transition with a reviewer-tier permission.
 *
 * BIO-308 #5a: **Approved is where the lifecycle ends, but it does not lock.**
 * The portal does not file with EMTS — the customer does, from the CSVs it
 * produces — so the Submitted/Frozen states and the restatement lane that
 * existed to work around their immutability were deleted outright. An approved
 * batch still accepts uploads, recomputes and takes corrections; if it needs to
 * change, it changes.
 *
 * Role gating mirrors TRANSITION_PERMISSIONS: sending for approval is
 * ops+reviewer+admin; approving and sending back are reviewer/admin. The BE is
 * the authoritative gate — the FE disables what the role/state obviously
 * forbids and surfaces any server rejection (a must-resolve blocker, an overdue
 * instrument, a stale validation run) as a notice.
 */

import { Button, Group, Menu, Tooltip } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconArrowBackUp,
  IconArrowRight,
  IconCircleCheckFilled,
  IconDots,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCalibrationReadiness, useTransitionBatch, useValidationStatus } from "../../api/hooks";
import type { BatchDetail, BatchState, ValidationStatus } from "../../api/types";
import { useHasRole } from "../../auth";
import { STATE_LABEL } from "../../components/BatchStateBadge";
import { DisabledReasonTooltip } from "../../components/DisabledReasonTooltip";
import { ReasonModal } from "../../components/ReasonModal";
import {
  GATED_TARGET,
  approvalBlocked,
  calibrationBlockedReason,
  calibrationBlockers,
} from "../../lib/calibrationReadiness";
import { invalidateBatchData, refreshInBackground } from "../../lib/invalidate";
import { queryKeys } from "../../lib/queryKeys";

/** Forward edge available from each state. Approving is one of them now — the
 * dedicated sign-off flow it used to belong to is gone. */
const FORWARD: Partial<Record<BatchState, { to: BatchState; label: string }>> = {
  Open: { to: "PendingApproval", label: "Send for approval" },
  PendingApproval: { to: "Approved", label: "Approve" },
};

/** Send-back (generic) edge available from each state. */
const SEND_BACK: Partial<Record<BatchState, { to: BatchState; label: string }>> = {
  PendingApproval: { to: "Open", label: "Send back to Open" },
  Approved: { to: "Open", label: "Send back to Open" },
};

/**
 * Why the forward edge is refused on a stale validation verdict.
 *
 * Leads with the day count when there is one. "10 days of data have arrived"
 * tells an operator both what is wrong and that time alone caused it; the generic
 * sentence sends them hunting for an upload or a correction that may not exist.
 * Mirrors the backend's own message so the tooltip and the 422 agree.
 */
function staleValidationReason(status: ValidationStatus): string {
  const days = status.new_data_days ?? 0;
  if (status.stale_reasons.includes("period_advanced") && days > 0) {
    const counted = days === 1 ? "1 day of data has" : `${days} days of data have`;
    return `${counted} arrived in this period since validation last ran, so the findings don't cover it. Re-run validation before advancing.`;
  }
  if (status.stale_reasons.includes("telemetry_changed")) {
    return "The meter data behind this batch has changed since validation last ran. Re-run validation before advancing.";
  }
  return "The data-quality findings were checked against earlier data. Re-run validation before advancing.";
}

export function LifecycleActions({ batch }: { batch: BatchDetail }) {
  const isOpsPlus = useHasRole("operations", "reviewer", "admin");
  const isReviewerPlus = useHasRole("reviewer", "admin");

  const transition = useTransitionBatch();
  const queryClient = useQueryClient();
  const _navigate = useNavigate();

  const forward = FORWARD[batch.state];
  const sendBack = SEND_BACK[batch.state];

  // Calibration readiness for the period. The BE refuses the forward edge when
  // `blocking_count > 0` (BIO-309); reading the same number keeps the button's
  // state and the server's answer from disagreeing, and lets the tooltip name the
  // instrument instead of leaving the operator to discover it by clicking. Only
  // fetched when that edge is actually the one available — every other batch
  // state would just discard the result.
  const readiness = useCalibrationReadiness(
    forward?.to === GATED_TARGET ? batch.batch_id : undefined,
  );

  // Validation freshness — the gate's third axis. It used to live in sign-off,
  // and moved onto the transition when sign-off was deleted, so the button that
  // triggers the transition is where it has to be said. Fetched whenever there
  // is a forward edge at all: BOTH forward edges are gated on it, not just the
  // calibration-gated one.
  const validationQ = useValidationStatus(forward ? batch.batch_id : undefined);

  // Pending generic transition awaiting a reason (forward or send-back).
  const [pendingTransition, setPendingTransition] = useState<{
    to: BatchState;
    label: string;
  } | null>(null);

  const refreshBatch = (updated?: BatchDetail) => {
    // Push the new metadata in immediately so the state badge updates without
    // waiting for the refetch, then invalidate the whole batch surface (audit,
    // reports, issues, calc-results) since a lifecycle move ripples into them.
    // The write is synchronous and the invalidation is not awaited: a caller
    // closing its modal must not wait on a refetch to do it.
    if (updated) {
      queryClient.setQueryData(queryKeys.batches.detail(batch.batch_id), updated);
    }
    refreshInBackground(invalidateBatchData(queryClient, batch.batch_id));
  };

  // Every send-back is reviewer/admin now. The one ops-tier send-back was
  // DataReview → Open; with that state gone both survivors come off the approval
  // lane and carry BATCH_SEND_BACK, which is reviewer+ on the BE. Forward to
  // PendingApproval stays ops+ (the BE adds the must-resolve gate on top).
  const sendBackAllowed = !!sendBack && isReviewerPlus;

  // Only the approval edge is gated on calibration (see lib/calibrationReadiness).
  // The disabled state comes from the BE's own approval_blocked — readiness can be
  // unavailable, which it refuses with no verdicts to list.
  const blockers = calibrationBlockers(readiness.data, forward?.to);
  const blocked = approvalBlocked(readiness.data, forward?.to);
  // Calibration first: a stale run usually accompanies a real blocker, and
  // "re-run validation" is the wrong instruction when the problem is an overdue
  // meter. Mirrors the order the backend reports them in.
  const forwardBlockedReason = blocked
    ? calibrationBlockedReason(blockers, readiness.data?.unavailable_reason)
    : validationQ.data?.stale
      ? staleValidationReason(validationQ.data)
      : null;

  const onConfirmTransition = async (reason: string) => {
    if (!pendingTransition) return;
    try {
      const updated = await transition.mutateAsync({
        batchId: batch.batch_id,
        toState: pendingTransition.to,
        reason,
      });
      notifications.show({
        title: `Moved to ${STATE_LABEL[pendingTransition.to]}`,
        message: batch.batch_id,
        color: "teal",
        icon: <IconCircleCheckFilled size={18} />,
      });
      refreshBatch(updated);
      setPendingTransition(null);
    } catch {
      // Surfaced inline via `transition.error`; don't rethrow — this runs in a
      // click handler, so a rethrow would escape as an unhandled rejection.
    }
  };

  const nothingAvailable = !forward && !sendBack;
  if (nothingAvailable) return null;

  return (
    <>
      <Group gap="xs">
        {forward ? (
          <RoleButton
            tour="lifecycle-forward"
            allowed={isOpsPlus}
            blockedReason={forwardBlockedReason}
            label={forward.label}
            icon={<IconArrowRight size={16} />}
            onClick={() => setPendingTransition(forward)}
          />
        ) : null}

        {sendBack ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Tooltip label="More lifecycle actions" position="bottom">
                <Button
                  variant="default"
                  px="xs"
                  aria-label="More lifecycle actions"
                  data-tour="lifecycle-more"
                >
                  <IconDots size={16} />
                </Button>
              </Tooltip>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Send back</Menu.Label>
              <Menu.Item
                leftSection={<IconArrowBackUp size={14} />}
                disabled={!sendBackAllowed}
                onClick={() => setPendingTransition(sendBack)}
              >
                {sendBack.label}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null}
      </Group>

      <ReasonModal
        opened={pendingTransition !== null}
        title={pendingTransition?.label ?? "Transition batch"}
        description={
          pendingTransition
            ? `Move ${batch.batch_id} to ${STATE_LABEL[pendingTransition.to]}. Recorded in the audit log.`
            : ""
        }
        confirmLabel="Confirm"
        busy={transition.isPending}
        error={transition.error}
        onConfirm={onConfirmTransition}
        onClose={() => {
          if (transition.isPending) return;
          setPendingTransition(null);
          transition.reset();
        }}
      />
    </>
  );
}

const ROLE_DENIED_REASON = "Your role doesn't permit this action.";

function RoleButton({
  allowed,
  blockedReason = null,
  label,
  icon,
  color = "indigo",
  variant = "filled",
  onClick,
  tour,
}: {
  allowed: boolean;
  /** `data-tour` anchor for the guided demo tour. */
  tour?: string;
  /** Why the action is unavailable despite the role permitting it (e.g. a
   * calibration blocker). Disables the button and explains in the tooltip. */
  blockedReason?: string | null;
  label: string;
  icon: React.ReactNode;
  color?: string;
  variant?: string;
  onClick: () => void;
}) {
  const enabled = allowed && !blockedReason;
  // Role denial outranks the calibration remedy: telling a reader "record a
  // calibration" implies the action would then be theirs to take, which it
  // would not be.
  const reason = !allowed ? ROLE_DENIED_REASON : blockedReason;
  const button = (
    <Button
      leftSection={icon}
      color={color}
      variant={enabled ? variant : "light"}
      disabled={!enabled}
      onClick={onClick}
      data-tour={tour}
    >
      {label}
    </Button>
  );
  return (
    <DisabledReasonTooltip
      reason={enabled ? null : (reason ?? ROLE_DENIED_REASON)}
      position="bottom"
      w={280}
    >
      {button}
    </DisabledReasonTooltip>
  );
}
